import React, { useEffect, useRef, useState } from "react";
import { Search, X, User, Car as CarIcon, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CRM_API } from "../api";

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  // ⌘K / Ctrl+K to open
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setRes(null); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await CRM_API.get(`/search?q=${encodeURIComponent(q)}`);
        if (!cancelled) setRes(data);
      } catch {
        if (!cancelled) setRes({ customers: [], vehicles: [], services: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };

  return (
    <>
      <button
        data-testid="crm-search-trigger"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-white/60 hover:text-white transition-colors w-full sm:w-72 md:w-96"
      >
        <Search className="w-4 h-4" />
        <span className="text-[13px]">Search customers, VIN, plate…</span>
        <kbd className="ml-auto hidden md:inline-block font-mono-alt text-[9px] px-1.5 py-0.5 rounded border border-white/15 text-white/40">⌘K</kbd>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
          onClick={() => setOpen(false)}
          data-testid="crm-search-modal"
        >
          <div
            className="w-full max-w-2xl bg-[#131319] border border-white/12 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <Search className="w-5 h-5 text-white/40" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search anything…"
                className="flex-1 bg-transparent outline-none text-white placeholder-white/30"
                data-testid="crm-search-input"
              />
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-3">
              {loading && <div className="p-6 text-center text-white/40 text-sm">Searching…</div>}
              {!loading && !q && (
                <div className="p-6 text-center text-white/30 text-xs font-mono-alt uppercase tracking-widest">
                  Type to search customers · vehicles · services
                </div>
              )}
              {!loading && res && (
                <>
                  <ResGroup title="Customers" items={res.customers} icon={User} render={(c) => ({
                    label: `${c.first_name} ${c.last_name || ""}`.trim(),
                    hint: `${c.phone} · ${c.email || "—"}`,
                    path: `/crm/customers/${c.id}`,
                  })} onGo={go} testid="search-res-customers" />
                  <ResGroup title="Vehicles" items={res.vehicles} icon={CarIcon} render={(v) => ({
                    label: `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim() || v.vin || "Vehicle",
                    hint: [v.vin, v.license_plate].filter(Boolean).join(" · "),
                    path: `/crm/vehicles/${v.id}`,
                  })} onGo={go} testid="search-res-vehicles" />
                  <ResGroup title="Services" items={res.services} icon={Sparkles} render={(s) => ({
                    label: s.name,
                    hint: `${s.category} · $${s.price}`,
                    path: `/crm/services`,
                  })} onGo={go} testid="search-res-services" />
                  {res.customers?.length === 0 && res.vehicles?.length === 0 && res.services?.length === 0 && (
                    <div className="p-6 text-center text-white/30 text-sm">No results</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ResGroup({ title, items, icon: Icon, render, onGo, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="px-3 py-1.5 font-mono-alt text-[10px] uppercase tracking-widest text-white/40">
        {title}
      </div>
      {items.map((it) => {
        const r = render(it);
        return (
          <button
            key={it.id}
            onClick={() => onGo(r.path)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 text-left"
          >
            <Icon className="w-4 h-4 text-white/40" />
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm truncate">{r.label}</div>
              {r.hint && <div className="text-white/40 text-xs truncate">{r.hint}</div>}
            </div>
          </button>
        );
      })}
    </div>
  );
}
